import React from 'react'
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import DocsSidebar from './DocsSidebar'
import { docsSections } from '../routes/_config'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'

const normalizeDocsPath = (pathname) => {
  if (pathname === '/docs') return '/'
  if (pathname.startsWith('/docs/')) return pathname.replace('/docs', '')
  return pathname
}

const getSectionPages = (section) => {
  if (Array.isArray(section.groups)) {
    return section.groups.flatMap((group) => {
      if (group.component) {
        return [
          {
            ...group,
            groupId: null,
            groupTitle: null,
          },
        ]
      }

      return Array.isArray(group.pages)
        ? group.pages.map((page) => ({
            ...page,
            groupId: group.id,
            groupTitle: group.title,
          }))
        : []
    })
  }

  return Array.isArray(section.pages) ? section.pages : []
}

const findDocByPath = (pathname) => {
  const normalizedPath = normalizeDocsPath(pathname)

  for (const section of docsSections) {
    const pages = getSectionPages(section)

    const page = pages.find((p) => `/${section.id}/${p.id}` === normalizedPath)

    if (page) {
      return {
        ...page,
        sectionId: section.id,
        sectionTitle: section.title,
        groupTitle: page.groupTitle,
      }
    }
  }

  return null
}

// Flattened, ordered list of every doc page across every section, used to
// work out what "previous" and "next" mean from any given page.
const flatPageList = docsSections.flatMap((section) =>
  getSectionPages(section).map((page) => ({
    href: `/${section.id}/${page.id}`,
    sectionId: section.id,
    sectionTitle: section.title,
    title: page.title,
  })),
)

const DocsLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const currentDoc = findDocByPath(location.pathname)

  const breadcrumbs = [
    currentDoc?.sectionTitle,
    currentDoc?.groupTitle,
    currentDoc?.title,
  ].filter(Boolean)
  const title = currentDoc?.title || ''
  const description = currentDoc?.summary || ''
  const hideHero = currentDoc?.hideHero || false

  const normalizedPath = normalizeDocsPath(location.pathname)
  const currentIndex = flatPageList.findIndex((p) => p.href === normalizedPath)
  const prevPage = currentIndex > 0 ? flatPageList[currentIndex - 1] : null
  const nextPage =
    currentIndex >= 0 && currentIndex < flatPageList.length - 1
      ? flatPageList[currentIndex + 1]
      : null
  const docsBase = window.location.hostname.startsWith('docs.') ? '' : '/docs'
  const goToDoc = (href) => navigate(`${docsBase}${href}`)
  return (
    <Box
      sx={{
        display: 'grid',
        // minmax(0, …) on every breakpoint: a plain `1fr` track has an auto
        // minimum, so wide content (grids, images, long words) stretches the
        // column past the screen instead of wrapping inside it.
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: '280px minmax(0, 1fr)',
        },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      <DocsSidebar />

      <Paper
        elevation={0}
        sx={{
          minHeight: '65vh',
          borderRadius: { xs: 2.5, md: 3 },
          overflow: 'hidden',
          border: '1px solid rgba(15,23,42,0.07)',
          boxShadow: '0 16px 50px rgba(15,23,42,0.07)',
          bgcolor: '#FFFFFF',
        }}
      >
        <Stack divider={<Divider />} sx={{ height: '100%' }}>
          {!hideHero && title && (
            <Box
              sx={{
                p: { xs: 2.25, md: 3.5 },
                background:
                  'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1
                  // Only the section crumb (index 0) is clickable — it jumps
                  // to the first page in that section. Group/page crumbs
                  // don't have their own landing page to link to.
                  const isSectionCrumb = index === 0
                  const sectionFirstPage = isSectionCrumb
                    ? flatPageList.find(
                        (p) => p.sectionId === currentDoc?.sectionId,
                      )
                    : null

                  return (
                    <React.Fragment key={`${crumb}-${index}`}>
                      <Chip
                        label={crumb}
                        size="small"
                        clickable={!isLast && !!sectionFirstPage}
                        onClick={
                          !isLast && sectionFirstPage
                            ? () => goToDoc(sectionFirstPage.href)
                            : undefined
                        }
                        sx={{
                          height: 24,
                          borderRadius: 1.5,
                          fontWeight: 700,
                          fontSize: 11,
                          color: isLast ? '#242F40' : '#1E7F88',
                          bgcolor: isLast
                            ? 'rgba(15,23,42,0.05)'
                            : 'rgba(35,154,164,0.10)',
                          cursor:
                            !isLast && sectionFirstPage ? 'pointer' : 'default',
                        }}
                      />
                      {!isLast && (
                        <KeyboardArrowRightRoundedIcon
                          sx={{ fontSize: 16, color: '#94A3B8' }}
                        />
                      )}
                    </React.Fragment>
                  )
                })}
              </Stack>

              <Typography
                variant="h3"
                sx={{
                  letterSpacing: 0,
                  lineHeight: 1.18,
                  mb: 1,
                  fontSize: { xs: '1.35rem', md: '1.7rem' },
                  color: '#242F40',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 700,
                }}
              >
                {title}
              </Typography>

              {!!description && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.92rem', md: '1rem' },
                    lineHeight: 1.75,
                    maxWidth: 920,
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          )}

          {/* DOCS FORMAT MDX FORMAT */}
          <Box
            sx={{
              px: { xs: 2, sm: 3, lg: 4 },
              py: { xs: 2.5, lg: 4 },
              maxWidth: 980,
              width: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            <Outlet />

            {(prevPage || nextPage) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ mt: 5, pt: 3, borderTop: '1px solid rgba(15,23,42,0.08)' }}
              >
                <Box
                  onClick={prevPage ? () => goToDoc(prevPage.href) : undefined}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(15,23,42,0.08)',
                    visibility: prevPage ? 'visible' : 'hidden',
                    cursor: prevPage ? 'pointer' : 'default',
                    transition: '0.15s ease',
                    '&:hover': prevPage
                      ? { borderColor: '#239AA4', bgcolor: 'rgba(35,154,164,0.05)' }
                      : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ArrowBackRoundedIcon sx={{ fontSize: 16, color: '#239AA4' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Previous
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#242F40', mt: 0.5 }}>
                    {prevPage?.title}
                  </Typography>
                </Box>

                <Box
                  onClick={nextPage ? () => goToDoc(nextPage.href) : undefined}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(15,23,42,0.08)',
                    textAlign: { xs: 'left', sm: 'right' },
                    visibility: nextPage ? 'visible' : 'hidden',
                    cursor: nextPage ? 'pointer' : 'default',
                    transition: '0.15s ease',
                    '&:hover': nextPage
                      ? { borderColor: '#239AA4', bgcolor: 'rgba(35,154,164,0.05)' }
                      : undefined,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Next
                    </Typography>
                    <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: '#239AA4' }} />
                  </Stack>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#242F40', mt: 0.5 }}>
                    {nextPage?.title}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    </Box>
  )
}

export default DocsLayout
